import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import TopBar from './components/TopBar';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Auth Setup
import { AuthProvider, useAuth } from './utils/authContext';

// Auth Pages
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';

// Pages
import Home from './pages/Home.jsx';
import Gallery from './pages/Gallery';
import Videos from './pages/Videos';
import About from './pages/About';
import Contact from './pages/Contact';
import BlocksListing from './pages/BlocksListing';
import VillageDetail from './pages/VillageDetail';
import VillagePage from './pages/VillagePage';
import VillageNewsDetail from './pages/VillageNewsDetail';
import NewsDetail from './pages/NewsDetail.jsx';
import HomeNewsDetail from './pages/HomeNewsDetail';
import SearchResults from './pages/SearchResults';

// News Category Pages (NEW)
import ChitrakootNews from './pages/ChitrakootNews';
import MenuNewsPage from './components/MenuNewsPage';
import HomeCategoryPage from './components/HomeCategoryPage';

// Video Pages (NEW)
import ViralVideos from './pages/ViralVideos';
import ChaupalVideos from './pages/ChaupalVideos';

// Info Pages (NEW)
import Mission from './pages/Mission';
import Team from './pages/Team';
import Feedback from './pages/Feedback';

// Admin Pages
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import ManageVideos from './pages/ManageVideos';
import AdminNewsAdd from './pages/AdminNewsAdd';
import AdminHomeNewss from './pages/AdminHomeNewss';
import EditNews from './pages/EditNews';
import AdminOtherNews from './pages/AdminOtherNews';
import ManageBreakingHeadlines from './pages/ManageBreakingHeadlines';
import ManageChitrakootBlocks from './pages/ManageChitrakootBlocks';
import ManageDistricts from './pages/ManageDistricts';
import ManageVillages from './pages/ManageVillages';
import ManageTheme from './pages/ManageTheme';
import { loadAndApplyThemeSettings } from './utils/themeRuntime';

import './App.css';

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (user.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  useEffect(() => {
    loadAndApplyThemeSettings();
  }, []);

  // if (showSplash) {
  //   return <SplashScreen onFinish={() => setShowSplash(false)} />;
  // }

  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <TopBar />
          <Navbar />
          <ScrollToTop />

          <main>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <Navigate to="/admin/dashboard" replace />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/videos"
                element={
                  <AdminProtectedRoute>
                    <ManageVideos />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/news"
                element={
                  <AdminProtectedRoute>
                    <AdminNewsAdd />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/news/add"
                element={
                  <AdminProtectedRoute>
                    <AdminNewsAdd />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/home-newss"
                element={
                  <AdminProtectedRoute>
                    <AdminHomeNewss />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/news/edit/:id"
                element={
                  <AdminProtectedRoute>
                    <EditNews />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/other-news"
                element={
                  <AdminProtectedRoute>
                    <AdminOtherNews />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/breaking-headlines"
                element={
                  <AdminProtectedRoute>
                    <ManageBreakingHeadlines />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/theme"
                element={
                  <AdminProtectedRoute>
                    <ManageTheme />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/chitrakoot-blocks"
                element={
                  <AdminProtectedRoute>
                    <ManageChitrakootBlocks />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/manage-districts"
                element={
                  <AdminProtectedRoute>
                    <ManageDistricts />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/manage-villages"
                element={
                  <AdminProtectedRoute>
                    <ManageVillages />
                  </AdminProtectedRoute>
                }
              />
              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

              {/* Main Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blocks" element={<BlocksListing />} />
              <Route path="/village/:villageName" element={<VillagePage />} />
              <Route path="/village/:villageId" element={<VillageDetail />} />
              <Route path="/village-news/:newsId" element={<VillageNewsDetail />} />
              <Route path="/news/:slug" element={<NewsDetail />} />
              <Route path="/home-news/:slug" element={<HomeNewsDetail />} />
              <Route path="/search" element={<SearchResults />} />

              {/* News Category Routes (NEW) */}
              <Route
                path="/breaking-news"
                element={
                  <MenuNewsPage
                    menuTitle="देश-विदेश"
                    submenu="ब्रेकिंग न्यूज़"
                    pageTitle="ब्रेकिंग न्यूज़"
                    subtitle="देश और विदेश की ताजा खबरें"
                    emoji="🔥"
                  />
                }
              />
              <Route
                path="/up-news"
                element={
                  <MenuNewsPage
                    menuTitle="उत्तर प्रदेश"
                    submenu="बड़ी खबर"
                    pageTitle="उत्तर प्रदेश"
                    subtitle="प्रदेश की बड़ी और जरूरी खबरें"
                    emoji="🏛️"
                  />
                }
              />
              <Route path="/chitrakoot-breaking" element={<ChitrakootNews />} />
              <Route
                path="/politics"
                element={
                  <HomeCategoryPage
                    category="राजनीति"
                    pageTitle="राजनीति"
                    subtitle="राजनीतिक घटनाओं की प्रमुख कवरेज"
                    emoji="🗳️"
                  />
                }
              />
              <Route
                path="/education"
                element={
                  <HomeCategoryPage
                    category="शिक्षा"
                    pageTitle="शिक्षा"
                    subtitle="शिक्षा जगत की नई जानकारी और अपडेट"
                    emoji="📚"
                  />
                }
              />
              <Route path="/religion" element={<Navigate to="/raashi" replace />} />
              <Route
                path="/raashi"
                element={
                  <HomeCategoryPage
                    category="धर्म"
                    pageTitle="धर्म"
                    subtitle="धर्म और परंपरा से जुड़ी खबरें"
                    emoji="🙏"
                  />
                }
              />
              <Route
                path="/suvichar"
                element={
                  <MenuNewsPage
                    menuTitle="धर्म"
                    submenu="सुविचार"
                    pageTitle="सुविचार"
                    subtitle="प्रेरक और धार्मिक विचार"
                    emoji="🪔"
                  />
                }
              />
              <Route
                path="/entertainment"
                element={
                  <HomeCategoryPage
                    category="मनोरंजन"
                    pageTitle="मनोरंजन"
                    subtitle="फिल्म, संगीत और मनोरंजन की खबरें"
                    emoji="🎬"
                  />
                }
              />
              <Route
                path="/history"
                element={
                  <MenuNewsPage
                    menuTitle="इतिहास"
                    submenu="ज्ञानी बाबा"
                    pageTitle="इतिहास"
                    subtitle="इतिहास और विरासत से जुड़ी कहानियां"
                    emoji="📖"
                  />
                }
              />

              {/* Video Routes (NEW) */}
              <Route path="/viral-videos" element={<ViralVideos />} />
              <Route path="/chaupal-videos" element={<ChaupalVideos />} />

              {/* Info Routes (NEW) */}
              <Route path="/mission" element={<Mission />} />
              <Route path="/team" element={<Team />} />
              <Route path="/feedback" element={<Feedback />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

function NotFound() {
  return (
    <div style={{
      textAlign: 'center',
      padding: '100px 0',
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>🔍</h1>
        <h2>404 - पृष्ठ नहीं मिला</h2>
        <p>यह पृष्ठ उपलब्ध नहीं है।</p>

        <a
          href="/"
          style={{
            color: 'var(--primary-color)',
            textDecoration: 'none',
            fontWeight: 'bold'
          }}
        >
          होम पर वापस जाएं
        </a>

      </div>
    </div>
  );
}

export default App;